/**
 * OwnerStatements — admin page to preview + send monthly owner statements.
 * Route: `/admin/owner-statements`. Lists all landlords, lets you pick a
 * month, preview activity, download the PDF, and send single or bulk.
 */
import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Envelope, FilePdf, Users, PaperPlaneTilt, ArrowClockwise, Eye, Sparkle, ClipboardText } from "@phosphor-icons/react";

function prevMonthISO() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function OwnerStatements() {
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(prevMonthISO());
  const [selected, setSelected] = useState(null);   // landlord in preview
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [digest, setDigest] = useState(null);        // { digest, used_ai, tone }
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestTone, setDigestTone] = useState("friendly"); // friendly | formal | terse

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/landlords");
      setLandlords(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openPreview = async (ll) => {
    setSelected(ll);
    setPreview(null);
    setDigest(null);
    try {
      const r = await api.post("/owner-statements/preview", {
        landlord_id: ll.id, month,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runDigest = async () => {
    if (!selected) return;
    setDigestBusy(true);
    const t = toast.loading("Drafting your monthly digest…");
    try {
      const r = await api.post("/owner-statements/ai-digest", {
        landlord_id: selected.id, month, tone: digestTone,
      });
      setDigest(r.data);
      toast.success(r.data.used_ai ? "AI digest ready" : "Digest ready (fallback template)", { id: t });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Digest failed", { id: t });
    } finally { setDigestBusy(false); }
  };

  const copyDigest = async () => {
    if (!digest?.digest) return;
    try {
      await navigator.clipboard.writeText(digest.digest);
      toast.success("Digest copied to clipboard");
    } catch { toast.error("Could not copy — select the text manually"); }
  };

  const sendOne = async (ll) => {
    if (!window.confirm(`Send ${month} statement to ${ll.name} (${ll.email || "no email"})?`))
      return;
    setBusy(true);
    const t = toast.loading("Sending…");
    try {
      const r = await api.post("/owner-statements/send", {
        landlord_id: ll.id, month,
      });
      toast.success(`Sent to ${r.data.to} · ${r.data.wo_count} WOs · $${(r.data.funded_total_cents / 100).toFixed(2)}`, { id: t });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: t });
    } finally { setBusy(false); }
  };

  const sendBulk = async () => {
    if (!window.confirm(`Send ${month} statements to EVERY landlord with activity? This can't be undone.`))
      return;
    setBulkBusy(true);
    const t = toast.loading("Sending bulk statements…");
    try {
      const r = await api.post("/owner-statements/send-bulk", { month });
      toast.success(`Sent ${r.data.sent}/${r.data.attempted} statements for ${r.data.month}`, { id: t });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: t });
    } finally { setBulkBusy(false); }
  };

  const pdfUrl = (ll) =>
    `${API}/owner-statements/${ll.id}/pdf?month=${month}&auth=${encodeURIComponent(localStorage.getItem('access_token') || '')}`;

  const fmt = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="owner-statements-page">
        {/* Hero */}
        <div className="flex items-start gap-3 flex-wrap">
          <Envelope weight="fill" size={32} className="text-cyan-600"/>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-black text-3xl">Owner Statements</h1>
            <p className="text-sm text-slate-500">
              1st-of-month landlord recap — WOs done, $ spent, upcoming preventive maintenance.
              A cron sends these automatically; use this page to preview or send off-cycle.
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <label className="text-xs font-black uppercase tracking-widest text-slate-500">Statement month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 data-testid="statement-month-input"
                 className="h-11 px-3 border-2 border-[#0f172a] font-black text-sm"/>
          <button onClick={sendBulk} disabled={bulkBusy}
                  data-testid="send-bulk-btn"
                  className="ml-auto tactile-btn h-11 px-4 bg-yellow-500 hover:bg-yellow-600 text-[#0f172a] font-black uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-60 shadow-[3px_3px_0_#0f172a]">
            <PaperPlaneTilt weight="fill" size={14}/>
            {bulkBusy ? "Sending…" : "Send Statements to Every Landlord"}
          </button>
        </div>

        {/* Landlord list */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4">
          <div className="rugged-card p-0 overflow-hidden">
            <div className="p-3 bg-slate-100 border-b-2 border-slate-200 flex items-center gap-2">
              <Users weight="fill" size={16} className="text-slate-600"/>
              <div className="font-black uppercase text-xs tracking-widest">Landlords · {landlords.length}</div>
              <button onClick={load} disabled={loading}
                      className="ml-auto h-7 px-2 border-2 border-slate-300 hover:border-[#0f172a] text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                <ArrowClockwise size={11}/> Refresh
              </button>
            </div>
            {loading && <div className="p-6 text-slate-500 font-bold">Loading…</div>}
            {!loading && landlords.length === 0 && (
              <div className="p-6 text-slate-500 font-bold">No landlords on file yet.</div>
            )}
            {!loading && landlords.length > 0 && (
              <div className="divide-y divide-slate-200 max-h-[70vh] overflow-y-auto">
                {landlords.map((ll) => (
                  <div key={ll.id}
                       className={`p-3 flex items-center gap-2 hover:bg-slate-50 cursor-pointer ${selected?.id === ll.id ? "bg-cyan-50" : ""}`}
                       onClick={() => openPreview(ll)}
                       data-testid={`landlord-row-${ll.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-sm truncate">{ll.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{ll.email || "— no email —"}</div>
                    </div>
                    <a href={pdfUrl(ll)} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`landlord-pdf-${ll.id}`}
                       className="h-8 w-8 flex items-center justify-center border-2 border-slate-300 hover:border-[#0f172a]">
                      <FilePdf weight="fill" size={14}/>
                    </a>
                    <button onClick={(e) => { e.stopPropagation(); sendOne(ll); }}
                            disabled={!ll.email || busy}
                            data-testid={`landlord-send-${ll.id}`}
                            className="h-8 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                      <Envelope size={12}/> Send
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview panel */}
          <div className="rugged-card p-0 overflow-hidden">
            <div className="p-3 bg-slate-100 border-b-2 border-slate-200 flex items-center gap-2">
              <Eye weight="fill" size={16} className="text-slate-600"/>
              <div className="font-black uppercase text-xs tracking-widest">Preview</div>
              {selected && (
                <a href={pdfUrl(selected)} target="_blank" rel="noreferrer"
                   data-testid="preview-pdf-link"
                   className="ml-auto h-7 px-2 border-2 border-[#0f172a] bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                  <FilePdf size={11}/> PDF
                </a>
              )}
            </div>
            {!selected && (
              <div className="p-8 text-center text-slate-500 font-bold" data-testid="preview-empty">
                <Eye size={40} className="mx-auto text-slate-300 mb-2"/>
                Click a landlord on the left to preview their {month} statement.
              </div>
            )}
            {selected && !preview && (
              <div className="p-8 text-slate-500 font-bold">Loading preview…</div>
            )}
            {selected && preview && (
              <div className="p-5 space-y-4" data-testid="preview-body">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Statement for</div>
                  <div className="text-2xl font-black">{preview.landlord.name}</div>
                  <div className="text-xs text-slate-500">{preview.landlord.email || "— no email —"}</div>
                  <div className="text-xs text-slate-500">{preview.month_label}</div>
                </div>
                {preview.no_properties ? (
                  <div className="p-4 bg-amber-50 border-2 border-amber-500 text-sm font-medium">
                    No properties linked to this landlord yet.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="p-3 bg-yellow-100 border-2 border-yellow-500">
                        <div className="text-[10px] font-black uppercase tracking-widest text-yellow-800">Billed</div>
                        <div className="text-xl font-black text-yellow-900" data-testid="preview-total">
                          {fmt(preview.funded_total_cents)}
                        </div>
                      </div>
                      <div className="p-3 bg-cyan-50 border-2 border-cyan-500">
                        <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">WOs Done</div>
                        <div className="text-xl font-black text-cyan-900">{preview.wo_count}</div>
                      </div>
                      <div className="p-3 bg-emerald-50 border-2 border-emerald-500">
                        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Properties</div>
                        <div className="text-xl font-black text-emerald-900">{preview.properties_touched}</div>
                      </div>
                    </div>
                    {preview.categories?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">By Category</div>
                        <div className="divide-y divide-slate-200 border-2 border-slate-200">
                          {preview.categories.map((c) => (
                            <div key={c.category} className="flex justify-between px-3 py-1.5 text-sm">
                              <span className="font-bold">{c.category}</span>
                              <span className="font-black">{fmt(c.amount_cents)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {preview.upcoming_pm?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800 mb-1">
                          Upcoming Preventive Maintenance ({preview.upcoming_pm.length})
                        </div>
                        <div className="space-y-1">
                          {preview.upcoming_pm.slice(0, 8).map((t, i) => (
                            <div key={i} className="flex justify-between text-xs px-2 py-1 bg-emerald-50 border border-emerald-200">
                              <span><b>{t.task_label}</b> · {t.address}</span>
                              <span className="text-emerald-700 font-black">{t.next_due_at.slice(0, 10)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                <button onClick={() => sendOne(selected)}
                        disabled={!selected.email || busy}
                        data-testid="preview-send-btn"
                        className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40 shadow-[3px_3px_0_#0f172a]">
                  <PaperPlaneTilt weight="fill" size={14}/>
                  {busy ? "Sending…" : `Send ${preview.month_label} Statement`}
                </button>

                {/* AI Digest — preview-only, no live email */}
                {!preview.no_properties && (
                  <div className="mt-3 border-2 border-fuchsia-500 bg-gradient-to-br from-fuchsia-50 to-white" data-testid="ai-digest-block">
                    <div className="p-3 border-b-2 border-fuchsia-200 flex items-center gap-2 flex-wrap">
                      <Sparkle weight="fill" size={16} className="text-fuchsia-700"/>
                      <div className="font-black uppercase text-xs tracking-widest text-fuchsia-900">AI Monthly Digest</div>
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">preview only · no email sent</span>
                      <select value={digestTone} onChange={(e) => setDigestTone(e.target.value)}
                              data-testid="digest-tone-select"
                              className="ml-auto h-7 px-2 border-2 border-fuchsia-300 text-[10px] font-black uppercase tracking-widest bg-white">
                        <option value="friendly">Friendly</option>
                        <option value="formal">Formal</option>
                        <option value="terse">Terse</option>
                      </select>
                      <button onClick={runDigest} disabled={digestBusy}
                              data-testid="digest-generate-btn"
                              className="h-7 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                        <Sparkle size={11}/> {digestBusy ? "Drafting…" : digest ? "Re-draft" : "Draft with AI"}
                      </button>
                    </div>
                    {!digest && !digestBusy && (
                      <div className="p-4 text-xs text-slate-600 font-medium" data-testid="digest-empty">
                        Generate a plain-English recap of {preview.month_label} for <b>{preview.landlord.name}</b>. Great for
                        copy-pasting into a text or DM before you wire up live email sends.
                      </div>
                    )}
                    {digest && (
                      <div className="p-4 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap text-[10px] font-black uppercase tracking-widest text-slate-500">
                          <span className={`px-1.5 py-0.5 border ${digest.used_ai ? "bg-fuchsia-100 border-fuchsia-500 text-fuchsia-800" : "bg-slate-100 border-slate-400 text-slate-600"}`}>
                            {digest.used_ai ? "AI · Claude Sonnet 4.5" : "Fallback template"}
                          </span>
                          <button onClick={copyDigest} data-testid="digest-copy-btn"
                                  className="ml-auto h-7 px-2 border-2 border-slate-300 hover:border-fuchsia-600 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                            <ClipboardText size={11}/> Copy
                          </button>
                        </div>
                        <div className="p-3 bg-white border-2 border-slate-200 whitespace-pre-wrap text-sm text-slate-800 leading-relaxed"
                             data-testid="digest-body">
                          {digest.digest}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
